import { useEffect, useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import imageCompression from 'browser-image-compression';

const MAX_FILE_SIZE_MB = 0.5;
const MAX_DIMENSION = 1200;

export default function ImageUploader({
  onUpload,
  onRemove,
  resetKey,
  maxFiles,
  maxFileSize = MAX_FILE_SIZE_MB,
  fileNamePrefix = '',
}) {
  const getMaxDimension = maxFileSize => {
    if (maxFileSize <= 0.5) return 1200;
    if (maxFileSize <= 1) return 1600;
    return 2000;
  };

  const [images, setImages] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');

  // RESET IMAGES when resetKey changes
  useEffect(() => {
    // Revoke previews to avoid memory leaks
    images.forEach(img => URL.revokeObjectURL(img.preview));
    setImages([]);
    setError('');
  }, [resetKey]);

  const preserveFileName = (sourceFile, targetMimeType) => {
    const mimeType = targetMimeType || sourceFile?.type || 'image/jpeg';
    const originalName = sourceFile?.name || 'image';
    const hasExtension = originalName.includes('.');
    const extension = hasExtension
      ? originalName.slice(originalName.lastIndexOf('.'))
      : mimeType === 'image/png'
        ? '.png'
        : mimeType === 'image/webp'
          ? '.webp'
          : '.jpg';

    const baseName = fileNamePrefix
      ? fileNamePrefix
      : hasExtension
        ? originalName.slice(0, originalName.lastIndexOf('.'))
        : 'image';

    const safeName = `${baseName}${extension}`;

    return new File([sourceFile], safeName, {
      type: mimeType,
      lastModified: sourceFile?.lastModified || Date.now(),
    });
  };

  const optimizeImage = async file => {
    // Try WebP first (better compression)
    const webpOptions = {
      maxSizeMB: maxFileSize,
      maxWidthOrHeight: getMaxDimension(maxFileSize),
      useWebWorker: true,
      initialQuality: 0.8,
      fileType: 'image/webp',
      alsoApplyOnWebp: true,
    };

    try {
      const compressedFile = await imageCompression(file, webpOptions);
      return preserveFileName(compressedFile, compressedFile.type || 'image/webp');
    } catch (err) {
      console.warn('⚠️ WebP compression failed, falling back to JPEG:', err);

      // Fallback to JPEG
      const jpegOptions = {
        maxSizeMB: maxFileSize,
        maxWidthOrHeight: getMaxDimension(maxFileSize),
        useWebWorker: true,
        initialQuality: 0.85,
        fileType: 'image/jpeg',
      };

      try {
        const compressedFile = await imageCompression(file, jpegOptions);
        return preserveFileName(compressedFile, compressedFile.type || 'image/jpeg');
      } catch (jpegErr) {
        console.error('❌ JPEG fallback also failed:', jpegErr);
        return preserveFileName(file, file.type || 'image/jpeg');
      }
    }
  };

  const onDrop = useCallback(
    async acceptedFiles => {
      setIsProcessing(true);

      const processedFiles = [];
      for (const file of acceptedFiles) {
        const optimized = await optimizeImage(file);
        processedFiles.push({
          file: optimized,
          preview: URL.createObjectURL(optimized),
        });
      }

      setImages(prev => {
        const updated = [...prev, ...processedFiles];

        // poziv roditelju *nakon* što smo izračunali finalni niz
        if (onUpload) {
          onUpload(updated.map(i => i.file));
        }

        return updated;
      });

      setIsProcessing(false);
    },
    [onUpload]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': [] },
    multiple: !maxFiles || maxFiles > 1, // ako je maxFiles=1, zabranjuj multiple
  });

  const removeImage = index => {
    setImages(prev => {
      const updated = prev.filter((_, i) => i !== index);

      if (onRemove) onRemove(prev[index].file);

      return updated;
    });

    setError('');
  };

  return (
    <div style={{ width: '100%', position: 'relative' }}>
      {/* Processing overlay */}
      {isProcessing && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: 'rgba(255,255,255,0.7)',
            backdropFilter: 'blur(3px)',
            zIndex: 10,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '20px',
            fontWeight: 'bold',
          }}
        >
          <div className="spinner" />
          <span style={{ marginLeft: '10px' }}>Processing...</span>
        </div>
      )}

      {/* Upload Zone */}
      <div
        {...getRootProps()}
        style={{
          border: '2px dashed #aaa',
          padding: '20px',
          textAlign: 'center',
          borderRadius: '8px',
          cursor: 'pointer',
          background: isDragActive ? '#f3f3f3' : '#fff',
          opacity: maxFiles === 1 && images.length >= 1 ? 0.5 : 1,
          pointerEvents: maxFiles === 1 && images.length >= 1 ? 'none' : 'auto',
        }}
      >
        <input {...getInputProps()} />
        <p>
          {isDragActive
            ? 'Drop image here...'
            : maxFiles === 1
              ? 'Upload image (samo jedna slika)'
              : 'Upload images'}
        </p>
      </div>

      {error && <div className="text-danger mt-2">{error}</div>}
      {/* PREVIEW GRID */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
          gap: '12px',
          marginTop: '15px',
        }}
      >
        {images.map((img, index) => (
          <div key={index} style={{ position: 'relative' }}>
            <img
              src={img.preview}
              alt="preview"
              style={{
                maxWidth: '100%',
                height: 'auto',
                objectFit: 'contain',
              }}
            />

            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); removeImage(index); }}
              style={{
                position: 'absolute',
                top: 5,
                right: 5,
                background: 'rgba(0,0,0,0.6)',
                color: 'white',
                border: 'none',
                borderRadius: '50%',
                width: '25px',
                height: '25px',
                cursor: 'pointer',
              }}
            >
              ×
            </button>
          </div>
        ))}
      </div>

      {/* Spinner CSS */}
      <style>
        {`
        .spinner {
          width: 25px;
          height: 25px;
          border: 3px solid #ccc;
          border-top: 3px solid #333;
          border-radius: 50%;
          animation: spin 0.6s linear infinite;
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}
      </style>
    </div>
  );
}
